import React, { ReactElement } from 'react'
import { useTranslate } from 'src/localization/Trns'

import type { SpendLimitState } from './SpendLimitTile'

type LimitStateNoteProps = {
  useLimitsApi: boolean
  limitsState: SpendLimitState
  canApplyLimitChange: boolean
}

/**
 * Why the spend limit cannot be adjusted right now — permission, enrolment,
 * a failed read, or a read still in flight.
 *
 * Lives on the TILE, not inside the drawer. These four states used to render
 * inside `LimitAdjustSection`, which put them behind the "Adjust spend limit"
 * button after the flyout conversion: the entry point advertised an action,
 * and the drawer then explained that no action was possible. Keeping the
 * explanation inline means the tile still reads correctly without a click, and
 * the button can be gated off entirely (see `limitIsAdjustable`).
 *
 * Returns `null` when adjusting IS possible — the form in the drawer is the
 * answer then, not a note.
 */
export default function LimitStateNote(
  props: LimitStateNoteProps,
): ReactElement | null {
  const { useLimitsApi, limitsState, canApplyLimitChange } = props
  const translate = useTranslate()

  // The unconnected (preview) path has no ladder: its form always renders, so
  // there is nothing to explain away.
  if (!useLimitsApi) {
    return null
  }

  if (limitsState === 'loading') {
    return (
      <p className="SpendLimitTile-body" data-testid="spend-limit-loading">
        {translate('Loading')}
      </p>
    )
  }

  if (limitsState === 'error') {
    return (
      <p
        className="SpendLimitTile-body"
        role="alert"
        data-testid="spend-limit-load-error"
      >
        {translate(
          "We couldn't load this card's spend limit. Refresh to try again.",
        )}
      </p>
    )
  }

  if (limitsState === 'unavailable') {
    return (
      <p className="SpendLimitTile-body" data-testid="spend-limit-unavailable">
        {translate(
          'Spend limit is not available for this card. It is not enrolled in card management, or your role cannot view it.',
        )}
      </p>
    )
  }

  // Read and mutation gate independently — seeing the limit is not permission
  // to change it.
  if (!canApplyLimitChange) {
    return (
      <p className="SpendLimitTile-body" data-testid="spend-limit-read-only">
        {translate(
          'You do not have permission to change this card’s spend limit.',
        )}
      </p>
    )
  }

  return null
}

/**
 * Whether the "Adjust spend limit" button should render at all — the exact
 * complement of `LimitStateNote` returning a note.
 *
 * Deliberately NOT the same predicate as `showAdjustablePill`: the pill says
 * "this limit is adjustable in principle", which stays true while the read is
 * still in flight. This says "you can act on it right now", which does not.
 */
export function limitIsAdjustable(
  useLimitsApi: boolean,
  limitsState: SpendLimitState,
  canApplyLimitChange: boolean,
): boolean {
  if (!useLimitsApi) {
    return true
  }
  return limitsState === 'ready' && canApplyLimitChange
}
